import { v4 as uuidV4 } from 'uuid';
import { defaultGTMDataLayerPayload, GTM_EVENT_TYPES } from './constants';

/* eslint-disable no-console */

function gtmDataLayerHandler(payload) {
  if (!payload || typeof payload !== 'object') {
    console.warn('Invalid payload passed to dataLayer');
    return;
  }

  try {
    // Ensure dataLayer exists
    window.dataLayer = window.dataLayer || [];

    // Push payload
    window.dataLayer.push(payload);
  } catch (error) {
    console.error('Error pushing data to GTM dataLayer:', error);
  }
}

/*

* This common function is used to track the time stamp for the GTM event
* @returns {Object} - Object containing the timestamp in the format of ISO Format and Local Format and Unix Timestamp
* timestamp will be part of 'pageview' | 'user_interaction' | 'user_exception'
*/
function gtmTrackTimeStampHandler() {
  const now = new Date();

  // ISO format (UTC) -> e.g. 2026-04-15T12:34:56.789Z
  const transaction_dt = now.toISOString();

  // Local format -> YYYY-MM-DDThh:mm:ss.ms
  const pad = (num, size = 2) => String(num).padStart(size, '0');

  const transaction_local_dt =
    now.getFullYear() + '-' +
    pad(now.getMonth() + 1) + '-' +
    pad(now.getDate()) + 'T' +
    pad(now.getHours()) + ':' +
    pad(now.getMinutes()) + ':' +
    pad(now.getSeconds()) + '.' +
    pad(now.getMilliseconds(), 3);

  // Unix timestamp (in seconds)
  const event_timestamp = Math.floor(now.getTime() / 1000);

  return {
    event_timestamp, // 1744750530 | Unix Timestamp
    transaction_dt, //  "2026-04-15T10:25:30.123Z" | ISO Format
    transaction_local_dt, //  "2026-04-15T10:25:30.123" | Local Format
  };
}


function toCamelCase(str) {
  return str.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

export function convertKeysToCamelCase(data) {
  if (Array.isArray(data)) {
    return data.map(convertKeysToCamelCase);
  }

  if (data !== null && typeof data === "object") {
    return Object.keys(data).reduce((acc, key) => {
      const camelKey = key.includes("_") ? toCamelCase(key) : key;
      acc[camelKey] = convertKeysToCamelCase(data[key]);
      return acc;
    }, {});
  }

  return data;
}

export function gtmTrackPageView(payload) {
  try {
    const {
      product_id,
      product_platform_code,
      product_model_name,
      business_model_code,
    } = defaultGTMDataLayerPayload;

    const { pageName = null, courseId = null, courseSectionId = null, contentId = null, contentType = null } = payload;
    const pageviewObj = {
      event: GTM_EVENT_TYPES.PAGEVIEW,
      page_title: pageName,
      business_model_code, // Product's Business Model Code. Ex: directToLearner, courseWare
      course_id: courseId,
      course_section_id: courseSectionId,
      content_id: contentId,
      content_id_type: contentType,

      product_model_name, // Product Model. Ex: REVEL, ETEXT2_PXE, ETEXT2_CITE
      message_id: uuidV4(), // Message Id. Should be in UUID Format
      product_id, // Product Id as UUID
      product_platform_code, // Product Platform. Ex: GLP, ILP, MASTERING
      ...gtmTrackTimeStampHandler(), // Timestamp
    };
    gtmDataLayerHandler(pageviewObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

export function gtmTrackGlobalLaunchEventHandler(payload) {
  try {
    const { app_id, app_version } = payload;
    const globalLaunchObj = {
      event: 'global_user',
      app_id,
      app_version,
    };
    gtmDataLayerHandler(globalLaunchObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error GTM tracking global launch:', error);
  }
}

export function gtmTrackGlobalObligatoryLaunchEventHandler(payload) {
  try {
    const {
      personIdType = null,
      event_action,
      personId = null,
      personRoleCode = null,
      sessionId = null,
    } = payload;

    const {
      secondary_person_id,
      secondary_person_id_type,
      secondary_person_role_code,
    } = defaultGTMDataLayerPayload;

    const globalObligatoryLaunchObj = {
      event: 'global_user',
      event_action,
      person_id: personId,
      person_id_type: personIdType,
      person_role_code: personRoleCode,
      secondary_person_id,
      secondary_person_id_type,
      secondary_person_role_code,
      login_session_id: sessionId,
      ...gtmTrackTimeStampHandler(), // Timestamp
    };

    // Push the payload to the GTM dataLayer
    gtmDataLayerHandler(globalObligatoryLaunchObj);
  } catch (error) {
    console.error('Error GTM tracking global obligatory launch:', error);
  }
}


export function gtmTrackUserInteractionActionEventHandler(payload){
  try{
    const { eventCategory = null, eventAction = null, eventLabel = null, eventValue = null } = payload;
    const telemetryPageActionEventObj = {
      event: GTM_EVENT_TYPES.USER_INTERACTION,
      event_category: eventCategory,
      event_action: eventAction,
      event_label: eventLabel,
      event_value: eventValue,
      ...gtmTrackTimeStampHandler(), // Timestamp
    };
    gtmDataLayerHandler(telemetryPageActionEventObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error GTM tracking user action:', error);
  }
}


export function gtmTrackUserExceptionEventHandler(payload){
  try{
    const { errorCode = null, errorUrl = null, errorMessage = null } = payload;
    const userExceptionObj = {
      event: GTM_EVENT_TYPES.USER_EXCEPTION,
      error_code: errorCode,
      error_url: errorUrl,
      error_message: errorMessage,
      ...gtmTrackTimeStampHandler(), // Timestamp
    };
    gtmDataLayerHandler(userExceptionObj); // Push the payload to the GTM dataLayer
  } catch (error) {
    console.error('Error GTM tracking user exception:', error);
  }
}